import React, { useState } from "react"
import groupBy from "lodash/groupBy"
import { useSwipeable } from "react-swipeable"

import { Element } from "legacy/components/shared/base"
import { ChallengeCarouselType } from "types/shared-components"
import { Controls } from "./Controls"
import { Maybe } from "types/Maybe"
import RichText from "legacy/components/shared/RichText/RichText"
import { Slide } from "./Slide"
import { Title } from "legacy/components/Redesign/Title/Title"

export type CarouselProps = {
  data: Maybe<ChallengeCarouselType>
}

const transitionFor = (property: string): string => `${property} 300ms ease`

export const Carousel: React.FC<CarouselProps> = ({
  data,
}): React.ReactElement | null => {
  const { items, primary } = data

  if (!primary || !items) return null

  const { carousel_intro, carousel_title, small_challenge_images, title_size } =
    primary

  const orderedSlideData = groupBy(items, "show_on_slide")

  const slides = Object.values(orderedSlideData)

  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)

  let handlers = undefined

  const slideCount = slides.length

  const onSwipedLeft = (): void => {
    if (slideCount !== currentSlideIndex + 1) {
      setCurrentSlideIndex(currentSlideIndex + 1)
    }
  }

  const onSwipedRight = (): void => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1)
    }
  }

  handlers = useSwipeable({
    onSwipedLeft,
    onSwipedRight,
    preventScrollOnSwipe: true,
  })

  return (
    <Element bg="lightCream" maxWidth="footerWidth" mx="auto">
      <Element
        maxWidth="contentWidth"
        mx="auto"
        display={["block", "grid"]}
        gridGap="sm"
        gridTemplateColumns={["none", "1fr 1fr"]}
        py={["md", "xlg"]}
        px="sm"
      >
        <Element textAlign={["center", "left"]}>
          {carousel_title?.text && (
            <Title
              mb="md"
              title={carousel_title?.text}
              titleSize={title_size}
            />
          )}
          {carousel_intro && (
            <RichText
              color="offBlack"
              fontFamily="martel"
              fontSize={["17px", "18px"]}
              fontWeight="400"
              lineHeight={["28px", "32px"]}
              mb="md"
              render={carousel_intro?.richText}
            />
          )}
          <Controls
            changeSlide={setCurrentSlideIndex}
            currentSlideIndex={currentSlideIndex}
            length={slides.length}
          />
        </Element>
        <Element
          {...handlers}
          overflow="hidden"
          style={{ touchAction: "pan-y" }}
        >
          <Element
            display="grid"
            gridTemplateColumns={`repeat(${slideCount}, 100%)`}
            flexWrap="nowrap"
            style={{
              transform: `translateX(-${currentSlideIndex * 100}%)`,
              transition: transitionFor("transform"),
            }}
            width="100%"
          >
            {slides.map((challenges, index) => {
              const title = primary[`slide_title_${index + 1}`]

              return (
                <Slide
                  challenges={challenges}
                  key={index}
                  smallImages={small_challenge_images}
                  title={title}
                />
              )
            })}
          </Element>
        </Element>
      </Element>
    </Element>
  )
}
